import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Tracks which popup surfaces are up so a lower-priority one can hold back
// rather than open under another's backdrop. Surfaces ask (useOutranked) and
// decide for themselves what holding back means; nothing here closes anything.
export const DashboardOverlay = {
  // The tour dims the page and reads any click outside its spotlight as "skip",
  // so a popup appearing over it can end the tour outright.
  Walkthrough: 'walkthrough',
  Modal: 'modal',
  Feedback: 'feedback',
} as const;

export type DashboardOverlayKind =
  (typeof DashboardOverlay)[keyof typeof DashboardOverlay];

const PRIORITY: Record<DashboardOverlayKind, number> = {
  [DashboardOverlay.Walkthrough]: 3,
  [DashboardOverlay.Modal]: 2,
  [DashboardOverlay.Feedback]: 1,
};

const OVERLAY_KINDS = Object.keys(PRIORITY) as DashboardOverlayKind[];

// Counts, not booleans: two components can render the same kind.
type OverlayCounts = Partial<Record<DashboardOverlayKind, number>>;

interface OverlayRegistry {
  counts: OverlayCounts;
  open: (kind: DashboardOverlayKind) => void;
  close: (kind: DashboardOverlayKind) => void;
}

// Default keeps consumers working outside the provider (unit tests, stories).
const OverlayContext = createContext<OverlayRegistry>({
  counts: {},
  open: () => undefined,
  close: () => undefined,
});

export const DashboardOverlayProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [counts, setCounts] = useState<OverlayCounts>({});

  const open = useCallback((kind: DashboardOverlayKind) => {
    setCounts((prev) => ({ ...prev, [kind]: (prev[kind] ?? 0) + 1 }));
  }, []);

  const close = useCallback((kind: DashboardOverlayKind) => {
    setCounts((prev) => ({
      ...prev,
      [kind]: Math.max((prev[kind] ?? 0) - 1, 0),
    }));
  }, []);

  const value = useMemo(() => ({ counts, open, close }), [counts, open, close]);

  return (
    <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>
  );
};

// Declare that this component is showing `kind` while `visible` holds. Register
// every surface: a surface is only outranked by overlays that announce themselves.
export function useRegisterOverlay(
  kind: DashboardOverlayKind,
  visible: boolean,
): void {
  const { open, close } = useContext(OverlayContext);

  useEffect(() => {
    if (!visible) return undefined;
    open(kind);
    return () => close(kind);
  }, [kind, visible, open, close]);
}

// Whether something higher-priority is up, i.e. whether `kind` should hold back.
export function useOutranked(kind: DashboardOverlayKind): boolean {
  const { counts } = useContext(OverlayContext);

  return OVERLAY_KINDS.some(
    (other) => PRIORITY[other] > PRIORITY[kind] && (counts[other] ?? 0) > 0,
  );
}
